import { divisionTables, slotContext } from "./standings";
import {
  type Division,
  effectiveLabel,
  gameSlots,
  type Pool,
  resolveSlot,
  type ScheduledGame,
  type Team,
} from "./types";

/**
 * One team's record at one tournament, for the public team profile
 * (board redesign, round 15): what it played, how it did in its pool, and
 * where it finished once the bracket had said so.
 *
 * Pure and shared: the Worker answers `GET /api/t/:tid/teams/:id/record`
 * with it, and a test pins the placement rules below, which are the part a
 * reader will argue with.
 */
export interface TeamRecord {
  teamId: string;
  /** The division's NAME, or null for a team in none. */
  division: string | null;
  /** The pool's name (a group title), null when the division has no pools
   *  or the team sits in none. */
  pool: string | null;
  played: number;
  wins: number;
  losses: number;
  draws: number;
  /** Point differential over every game with a result, bracket included. */
  diff: number;
  /** 1-based in its pool table, null when there is no table to rank in. */
  poolRank: number | null;
  poolSize: number | null;
  /**
   * The finish the bracket gave it, or null.
   *
   * From the LAST game that decides a finish: the final (winner 1, loser
   * 2), the third-place game (3, 4), a placement game for place P (P, P+1).
   * A team eliminated in a round that decides nothing - a semifinal with no
   * third-place game, a quarterfinal in a single-elimination draw - has no
   * finish here rather than a guessed one; its pool rank still stands.
   */
  placement: number | null;
}

/**
 * Every team's finish at the tournament, keyed by team id: the spirit table's
 * `placement` separator reads it (2026-09-11).
 *
 * The bracket finish where a deciding game gave one - `teamRecord`'s rule,
 * so the spirit table and the team's own page never disagree about a finish.
 * On a division where NO game decided a finish (a pool-only event, a Swiss
 * draw) the table IS the placement, so the pool rank stands in. A division
 * with a bracket gets no such fallback: a pool winner knocked out in a
 * semifinal with no third-place game has no finish, and handing it its pool
 * rank would put it above the runner-up. Absent from the map means "no
 * finish"; the separator ranks such a team below every team with one.
 */
export function tournamentPlacements(input: {
  teams: Team[];
  schedule: ScheduledGame[];
  divisions: Division[];
  pools: Pool[];
}): Map<string, number> {
  const records = new Map<string, TeamRecord>();
  for (const team of input.teams) {
    const record = teamRecord({ ...input, teamId: team.id });
    if (record !== null) records.set(team.id, record);
  }
  const decided = new Set<string | undefined>();
  for (const team of input.teams) {
    if (records.get(team.id)?.placement != null) decided.add(team.divisionId);
  }
  const out = new Map<string, number>();
  for (const team of input.teams) {
    const record = records.get(team.id);
    if (record === undefined) continue;
    const finish = decided.has(team.divisionId) ? record.placement : record.poolRank;
    if (finish !== null) out.set(team.id, finish);
  }
  return out;
}

export function teamRecord(input: {
  teamId: string;
  teams: Team[];
  schedule: ScheduledGame[];
  divisions: Division[];
  pools: Pool[];
}): TeamRecord | null {
  const { teamId, teams, schedule, divisions, pools } = input;
  const team = teams.find((t) => t.id === teamId);
  if (team === undefined) return null;

  const ctx = slotContext(teams, schedule);
  let played = 0;
  let wins = 0;
  let losses = 0;
  let draws = 0;
  let diff = 0;
  let placement: number | null = null;
  let placementAt = "";

  for (const game of [...schedule].sort((a, b) => a.startAt.localeCompare(b.startAt))) {
    const result = game.result;
    if (result === undefined) continue;
    const slots = gameSlots(game);
    const a = resolveSlot(slots.a, ctx);
    const b = resolveSlot(slots.b, ctx);
    const isA = a?.id === teamId;
    const isB = b?.id === teamId;
    if (!isA && !isB) continue;
    const mine = isA ? result.scoreA : result.scoreB;
    const theirs = isA ? result.scoreB : result.scoreA;
    played += 1;
    diff += mine - theirs;
    if (mine > theirs) wins += 1;
    else if (mine < theirs) losses += 1;
    else draws += 1;

    if (game.stage !== "bracket" || mine === theirs) continue;
    const finish = finishOf(effectiveLabel(game.note, game.label), mine > theirs);
    // The latest deciding game wins the argument: a team that lost a
    // semifinal and then won the third-place game finished third.
    if (finish !== null && game.startAt >= placementAt) {
      placement = finish;
      placementAt = game.startAt;
    }
  }

  const division = divisions.find((d) => d.id === team.divisionId) ?? null;
  let pool: string | null = null;
  let poolRank: number | null = null;
  let poolSize: number | null = null;
  if (division !== null) {
    // POOL games only, the way `seedOrder` reads the table: a bracket result
    // must not re-rank the pool that produced it.
    const poolGames = schedule.filter((g) => g.stage !== "bracket");
    for (const group of divisionTables(teams, poolGames, division.id, pools)) {
      const i = group.rows.findIndex((row) => row.team.id === teamId);
      if (i === -1) continue;
      pool = group.title;
      poolRank = i + 1;
      poolSize = group.rows.length;
      break;
    }
  }

  return {
    teamId,
    division: division?.name ?? null,
    pool,
    played,
    wins,
    losses,
    draws,
    diff,
    poolRank,
    poolSize,
    placement,
  };
}

/** The finish a deciding game hands its winner or its loser, or null for a
 *  round that decides nothing by itself. */
function finishOf(
  label: ReturnType<typeof effectiveLabel>,
  won: boolean,
): number | null {
  if (label === undefined) return null;
  switch (label.kind) {
    case "final":
    case "grandFinal":
      return won ? 1 : 2;
    case "thirdPlace":
      return won ? 3 : 4;
    case "placement":
      return won ? label.place : label.place + 1;
    default:
      return null;
  }
}
